/**
 * ImportLauncherDialog – Phase 5 Launcher Import
 *
 * Browse tenant users, preview their launcher layouts, and import
 * any layout as a new workspace draft.
 *
 * Flow:
 *   Select user → preview layout metadata → name workspace → Import
 *   → POST /import/create → navigate to /studio/:workspaceId
 *
 * RBAC: TA = own tenant only. SA = cross-tenant (tenantId prop required).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../../contexts/AuthContext';
import { useDealerAdmin } from '../../../contexts/DealerAdminContext';
import {
  listImportSources,
  getImportSource,
  createWorkspaceFromImport,
} from '../../../api/workspaceApi';
import { ROUTES } from '../../../utils/constants';
import { userLabel } from '../../../utils/userLabel';

export default function ImportLauncherDialog({ open, onClose, targetTenantId }) {
  const { userData, userClaims } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const navigate = useNavigate();

  const tenantId = targetTenantId || dealerAdmin.tenantId || userData?.tenantId;
  const role     = userData?.role || userClaims?.role;

  const [searchQ,       setSearchQ]       = useState('');
  const [sources,       setSources]       = useState([]);
  const [loadingSrcs,   setLoadingSrcs]   = useState(false);
  const [sourcesError,  setSourcesError]  = useState(null);

  const [selected,      setSelected]      = useState(null);   // source user item
  const [preview,       setPreview]       = useState(null);   // full layout
  const [loadingPrev,   setLoadingPrev]   = useState(false);

  const [wsName,        setWsName]        = useState('');
  const [importing,     setImporting]     = useState(false);
  const [importError,   setImportError]   = useState(null);

  const loadSources = useCallback(async (q = '') => {
    if (!tenantId) {
      setSources([]);
      setSourcesError(null);
      return;
    }
    setLoadingSrcs(true);
    setSourcesError(null);
    try {
      const resp  = await listImportSources({ tenantId, q: q || undefined });
      const items = resp?.data?.items ?? [];
      setSources(items);
    } catch (err) {
      const message = String(err?.message || '');
      setSourcesError(message.startsWith('API Error') ? 'Could not load users.' : (message || 'Could not load users.'));
    } finally {
      setLoadingSrcs(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open) {
      loadSources();
      setSelected(null);
      setPreview(null);
      setWsName('');
      setImportError(null);
    }
  }, [open, loadSources]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => loadSources(searchQ), 400);
    return () => clearTimeout(t);
  }, [searchQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectUser = async (source) => {
    setSelected(source);
    setWsName(`${userLabel(source)} workspace`);
    setPreview(null);
    setImportError(null);
    if (!source.hasLayout) return;
    setLoadingPrev(true);
    try {
      const resp = await getImportSource(source.uid, { tenantId });
      setPreview(resp?.data ?? null);
    } catch (err) {
      setPreview(null);
    } finally {
      setLoadingPrev(false);
    }
  };

  const handleImport = async () => {
    if (!selected || !wsName.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      const layoutData = preview?.layout || { icons: [] };
      const resp = await createWorkspaceFromImport({
        name:           wsName.trim(),
        sourceUserId:   selected.uid,
        sourceTenantId: tenantId,
        layoutData,
        targetTenantId: tenantId,
      });
      const ws = resp?.data?.workspace ?? resp?.workspace;
      if (ws?.id) {
        onClose();
        navigate(ROUTES.STUDIO_WORKSPACE.replace(':workspaceId', ws.id));
      }
    } catch (err) {
      setImportError(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const icons = preview?.layout?.icons ?? [];

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Import Launcher Layout
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Browse {tenantId ? `tenant users` : 'users'}, select a layout, and import it as a new
          workspace draft.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: 420, overflow: 'hidden' }}>

          {/* Left: user list */}
          <Box sx={{ width: 280, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search users…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider />
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {loadingSrcs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : sourcesError ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="error">{sourcesError}</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {sources.map((src) => (
                    <ListItem key={src.uid} disablePadding>
                      <ListItemButton
                        selected={selected?.uid === src.uid}
                        onClick={() => handleSelectUser(src)}
                      >
                        <ListItemText
                          primary={userLabel(src)}
                          secondary={src.email && userLabel(src) !== src.email ? src.email : null}
                          primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                          secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                        />
                        {src.hasLayout ? (
                          <Chip label={`${src.iconCount}ic`} size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        ) : (
                          <Chip label="no layout" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {!sources.length && !loadingSrcs && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">No users found.</Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          </Box>

          {/* Right: preview + import config */}
          <Box sx={{ flexGrow: 1, p: 2.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!selected && (
              <Box sx={{ m: 'auto', textAlign: 'center' }}>
                <DownloadIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Select a user to preview their launcher layout
                </Typography>
              </Box>
            )}

            {selected && (
              <>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Layout Preview — {userLabel(selected)}
                  </Typography>
                  {loadingPrev && <CircularProgress size={20} />}
                  {!loadingPrev && !selected.hasLayout && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      This user has no saved launcher layout. The workspace will start with default icons.
                    </Alert>
                  )}
                  {!loadingPrev && selected.hasLayout && preview && (
                    <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                      {icons.slice(0, 12).map((ic, i) => (
                        <Chip
                          key={ic.id || i}
                          label={ic.label || ic.id || `Icon ${i + 1}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {icons.length > 12 && (
                        <Chip label={`+${icons.length - 12} more`} size="small" color="default" />
                      )}
                      {icons.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No icons in this layout.</Typography>
                      )}
                    </Stack>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Workspace Name
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={wsName}
                    onChange={(e) => setWsName(e.target.value)}
                    placeholder="Enter a name for the new workspace…"
                    autoFocus
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    A new workspace draft will be created with this layout. You can edit it in Studio before publishing.
                    The original user's launcher is unaffected.
                  </Typography>
                </Box>

                {importError && (
                  <Alert severity="error">{importError}</Alert>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={importing}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!selected || !wsName.trim() || importing}
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        >
          {importing ? 'Importing…' : 'Import as Workspace'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
